import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IBranchExchangeModel } from 'src/app/logic/branch/models/branch-exchange.model';
import { IExchangeModel } from 'src/app/logic/exchanges/model/exchange.model';
import { ExchangeService } from 'src/app/services/exchange.service';
import { HttpService } from 'src/app/services/http-service.service';
import { LanguageService } from 'src/app/services/language.service';
import { ModalComponent } from '../modal/modal.component';
import {
  buildPromotionChips,
  buildPromotionHeadline,
  getBranchPhone,
  getBranchWhatsapp,
  getMapsUrl,
  PromotionChipView,
  PromotionDetailPromotion,
  PromotionHeadlineView,
} from './promotion-display.util';

interface PromotionAvailabilityPayload {
  operationLimit?: number;
  usedAmount?: number;
  availableAmount?: number;
  currencyCode?: string;
  currencyName?: string;
}

@Component({
  selector: 'app-promotion-detail-modal',
  templateUrl: './promotion-detail-modal.component.html',
  styleUrls: ['./promotion-detail-modal.component.scss'],
})
export class PromotionDetailModalComponent implements OnInit {
  headline: PromotionHeadlineView = {
    hasStructuredHeadline: false,
    headlineLead: '',
    oldPriceLabel: '',
    newPriceLabel: '',
    fallbackTitle: '',
    currencyLabel: '',
    operationLabel: '',
  };
  chips: PromotionChipView = {
    branchLabel: '',
    appliesToKey: '',
    availabilityLabel: '',
    validityLabel: '',
    discountLabel: '',
    showAppliesTo: false,
    showAvailability: false,
    showValidity: false,
    showDiscount: false,
  };

  mxnExchange: IExchangeModel | undefined;
  divisas: unknown;

  constructor(
    private readonly dialogRef: MatDialogRef<PromotionDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public promotion: PromotionDetailPromotion,
    private readonly dialog: MatDialog,
    private readonly exchangeService: ExchangeService,
    private readonly httpService: HttpService,
    private readonly translate: TranslateService,
    private readonly languageService: LanguageService
  ) {}

  ngOnInit(): void {
    const translateFn = (key: string, params?: Record<string, string>) =>
      this.translate.instant(key, params);
    const localeTag = this.languageService.getLocaleTag();
    this.headline = buildPromotionHeadline(this.promotion, translateFn, localeTag);
    this.chips = buildPromotionChips(this.promotion, translateFn, localeTag);
    this.loadRemainingAvailability(localeTag);

    const branchId = this.promotion.branch?.id;
    if (!branchId) {
      return;
    }

    this.exchangeService
      .getBranchExchangeRates(branchId)
      .subscribe(({ exchange }: IBranchExchangeModel) => {
        const rates = exchange as IExchangeModel[];
        this.mxnExchange = rates.find(
          (value) => value.currency?.code === 'MXN'
        );
        this.divisas = exchange;
      });
  }

  private loadRemainingAvailability(localeTag: string): void {
    const promotionId = this.promotion?.id;
    if (!promotionId) {
      return;
    }

    this.httpService.getPromotionAvailability(promotionId).subscribe({
      next: (response) => {
        const payload = (response as { data?: PromotionAvailabilityPayload })
          ?.data;
        if (payload?.availableAmount == null) {
          return;
        }

        const currency =
          payload.currencyCode ||
          payload.currencyName ||
          this.promotion.exchangeRate?.currency?.code ||
          this.promotion.exchangeRate?.currency?.name ||
          '';

        if (!currency) {
          return;
        }

        const availabilityLabel = this.translate.instant(
          'LANDING.PROMOTIONS.CARD_AVAILABILITY',
          {
            amount: new Intl.NumberFormat(localeTag).format(
              Number(payload.availableAmount),
            ),
            currency,
          },
        );

        this.chips = {
          ...this.chips,
          availabilityLabel,
          showAvailability: true,
        };
      },
      error: (error) => {
        console.error('Error loading promotion availability:', error);
      },
    });
  }

  get bannerSrc(): string {
    return this.promotion?.banner ?? './assets/img/image-not-found.png';
  }

  get bannerAlt(): string {
    return this.promotion?.title ?? '';
  }

  get branchPhone(): string {
    return getBranchPhone(this.promotion.branch);
  }

  get branchWhatsapp(): string {
    return getBranchWhatsapp(this.promotion.branch);
  }

  get mapsUrl(): string {
    return getMapsUrl(this.promotion.branch);
  }

  onDismiss(): void {
    this.dialogRef.close();
  }

  onReserve(): void {
    this.onDismiss();
    const isMobile = window.innerWidth < 768;
    const config: MatDialogConfig = {
      data: {
        sucursal: this.promotion.branch,
        promotion: this.promotion,
      },
      closeOnNavigation: true,
      disableClose: true,
      panelClass: ['custom-modalbox', 'promotion-detail-modalbox'],
      position: { bottom: '0', right: '0' },
      maxHeight: isMobile ? '85vh' : '70vh',
    };

    this.dialog.open(ModalComponent, config).afterClosed().subscribe();
  }
}
